import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const initialForm = {
  name: "",
  sort_order: "",
  is_active: true,
};

export default function MasterBanks() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const sortedBanks = useMemo(
    () =>
      [...banks].sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [banks]
  );

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/banks`, { withCredentials: true });
      setBanks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch banks:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data bank.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const openCreate = () => {
    setEditingBank(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEdit = (bank) => {
    setEditingBank(bank);
    setFormData({
      name: String(bank.name || ""),
      sort_order: String(bank.sort_order ?? ""),
      is_active: bank.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm("Hapus nama bank ini?")) return;
    try {
      await axios.delete(`${API}/banks/${bankId}`, { withCredentials: true });
      fetchBanks();
    } catch (error) {
      console.error("Failed to delete bank:", error);
      alert(error.response?.data?.detail || "Gagal menghapus bank.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(formData.name || "").trim().toUpperCase(),
      sort_order: Number(formData.sort_order || 0),
      is_active: !!formData.is_active,
    };
    if (!payload.name) {
      alert("Nama bank wajib diisi.");
      return;
    }

    try {
      if (editingBank) {
        await axios.put(`${API}/banks/${editingBank.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/banks`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingBank(null);
      setFormData(initialForm);
      fetchBanks();
    } catch (error) {
      console.error("Failed to save bank:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan bank.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Master Bank" />
      <div className="space-y-6 p-8" data-testid="master-bank-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Daftar Nama Bank</h2>
              <p className="mt-1 text-sm text-zinc-500">Kelola pilihan bank yang dipakai di Master PT dan Master Coach.</p>
            </div>
            <Button onClick={openCreate} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah Bank
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Nama Bank</TableHead>
                <TableHead>Sort Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">Loading...</TableCell>
                </TableRow>
              ) : sortedBanks.length > 0 ? (
                sortedBanks.map((bank, index) => (
                  <TableRow key={bank.id || index} className="border-white/5 hover:bg-zinc-900/50">
                    <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-4 text-sm font-semibold text-white">{bank.name}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{bank.sort_order || 0}</TableCell>
                    <TableCell className="py-4">
                      <Badge
                        variant="outline"
                        className={`rounded-sm text-xs ${
                          bank.is_active !== false
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                            : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                        }`}
                      >
                        {bank.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(bank)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                        >
                          <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(bank.id)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada data bank.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>{editingBank ? "Edit Bank" : "Tambah Bank"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Nama Bank</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                  placeholder="Contoh: BCA"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sort_order: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="flex items-center justify-between rounded-sm border border-white/10 bg-black/20 p-3">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 rounded-sm border-white/10 text-white hover:bg-zinc-800">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {editingBank ? "Update Bank" : "Simpan Bank"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
